import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

interface StripeConnectStatusResponse {
  success: boolean;
  data: {
    onboarded: boolean;
    account_id?: string | null;
    details_submitted?: boolean;
    charges_enabled?: boolean;
    payouts_enabled?: boolean;
    message?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TrainerStatusService {
  private isOnboardedSubject = new BehaviorSubject<boolean>(false);
  public isOnboarded$: Observable<boolean> = this.isOnboardedSubject.asObservable();
  private hasCheckedStatus = false;

  constructor(private http: HttpClient) {
    // Load from localStorage on service initialization
    this.loadFromStorage();
  }

  /**
   * Get current onboarded status (synchronous)
   */
  getIsOnboarded(): boolean {
    return this.isOnboardedSubject.value;
  }

  /**
   * True after this session has loaded status from the API (or set explicitly).
   * Do not treat localStorage-only values as verified until this is true.
   */
  hasVerifiedThisSession(): boolean {
    return this.hasCheckedStatus;
  }

  /**
   * Load status from localStorage (for instant display, but doesn't prevent API call)
   */
  private loadFromStorage(): void {
    const stored = localStorage.getItem('trainer_onboarded_status');
    if (stored !== null) {
      const isOnboarded = stored === 'true';
      this.isOnboardedSubject.next(isOnboarded);
      // Don't set hasCheckedStatus here - allow one API call per session to verify/refresh
    }
  }

  /**
   * Check Stripe connection status from API (call once per session unless forceRefresh is true)
   * @param forceRefresh When true, re-fetch from API even if already checked (e.g. pull-to-refresh)
   */
  checkStripeConnectStatus(forceRefresh = false): void {
    if (forceRefresh) {
      this.hasCheckedStatus = false;
    }
    // If already checked in this session, don't check again
    if (this.hasCheckedStatus) {
      return;
    }

    const token = localStorage.getItem('token');
    
    if (!token) {
      this.isOnboardedSubject.next(false);
      this.saveToStorage(false);
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json'
    });

    this.http.get<StripeConnectStatusResponse>(
      `${environment.apiUrl}/training/stripe-connect/status`,
      { headers }
    ).subscribe({
      next: (response) => {
        const isOnboarded = response.success && response.data.onboarded === true;
        this.isOnboardedSubject.next(isOnboarded);
        this.saveToStorage(isOnboarded);
        this.hasCheckedStatus = true;
      },
      error: (error) => {
        if (!environment.production) {
          console.error('Error checking Stripe Connect status:', error);
        }
        this.isOnboardedSubject.next(false);
        this.saveToStorage(false);
        this.hasCheckedStatus = true;
      }
    });
  }

  /**
   * Ensure status is loaded and return it once (for guards).
   * Uses cache if already checked this session; otherwise fetches once and updates cache.
   */
  getStatusOnce(): Observable<boolean> {
    if (this.hasCheckedStatus) {
      return of(this.getIsOnboarded());
    }
    const token = localStorage.getItem('token');
    if (!token) {
      this.isOnboardedSubject.next(false);
      this.saveToStorage(false);
      this.hasCheckedStatus = true;
      return of(false);
    }
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json'
    });
    return this.http.get<StripeConnectStatusResponse>(
      `${environment.apiUrl}/training/stripe-connect/status`,
      { headers }
    ).pipe(
      map((response) => {
        const isOnboarded = response.success && response.data.onboarded === true;
        this.isOnboardedSubject.next(isOnboarded);
        this.saveToStorage(isOnboarded);
        this.hasCheckedStatus = true;
        return isOnboarded;
      }),
      catchError((error) => {
        if (!environment.production) {
          console.error('Error checking Stripe Connect status:', error);
        }
        this.isOnboardedSubject.next(false);
        this.saveToStorage(false);
        this.hasCheckedStatus = true;
        return of(false);
      })
    );
  }

  /**
   * Save status to localStorage
   */
  private saveToStorage(isOnboarded: boolean): void {
    localStorage.setItem('trainer_onboarded_status', isOnboarded.toString());
  }

  /**
   * Update status (e.g., after successful onboarding)
   */
  setOnboardedStatus(isOnboarded: boolean): void {
    this.isOnboardedSubject.next(isOnboarded);
    this.saveToStorage(isOnboarded);
    this.hasCheckedStatus = true;
  }

  /**
   * Reset status (e.g., on logout)
   */
  resetStatus(): void {
    this.isOnboardedSubject.next(false);
    localStorage.removeItem('trainer_onboarded_status');
    this.hasCheckedStatus = false;
  }
}

